import React from "react";
import CustomTable from "../../../../../components/table/CustomTable";
import DiningAttendanceHead from "./DiningAttendanceHead";
import DiningAttendanceTableApp from "../Functionality/DiningAttendanceTableApp";
import FullPageLoader from "../../../../../utils/FullPageLoader";

/**
 * @description
 * DiningAttendanceTable renders a complete table view for dining attendance,
 * including a dynamic header with filters and search input, a customizable table,
 * and pagination. It relies on shared logic from `DiningAttendanceTableApp` and
 * subcomponents like `DiningAttendanceHead` and `CustomTable`.
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Software Developer
 * @Email: kpozos@ibl.mx
 *
 * @param {Object} props - Component props.
 * @param {string} props.subView - Current active subview ('dining' or 'department').
 * @param {Function} props.handleBtnDinning - Handler to activate dining subview.
 * @param {Function} props.handleBtnSettings - Callback to switch to settings view.
 * @param {Function} props.handleBtnDepartmentPolicies - Callback to switch to department policies view.
 * @param {Function} props.handleBtnTotalMeals - Callback to switch to total meals view.
 * @param {Array} props.finalArray - Final processed data array to display.
 * @param {Function} props.setFinalArray - Setter for updating final display data.
 * @param {Function} props.setBodyForExport - Setter for preparing export data.
 * @param {string} props.searchElement - Search input value.
 * @param {Function} props.setSearchElement - Setter for updating the search input.
 * @param {Function} props.setSearch - Function to trigger or update search logic.
 * @param {Function} props.setSimpleSearch - Setter for simple search flag/value.
 * @param {string} props.selectedOption - Selected filter/search option.
 * @param {Function} props.setSelectedOption - Setter to change selected option.
 * @param {JSX.Element|string} props.render - Optional content to render after table and pagination.
 * @param {Function} props.setRender - Setter to trigger re-render or reset render state.
 * @param {string} props.color - A color value used for table or UI theming.
 *
 * @returns {JSX.Element} The complete dining attendance table UI with filters, table, and pagination.
 */
const DiningAttendanceTable = (props) => {
    const {
        subView,
        handleBtnDinning,
        handleBtnSettings,
        handleBtnDepartmentPolicies,
        handleBtnTotalMeals,
        finalArray,
        setFinalArray,
        setBodyForExport,
        searchElement,
        setSearchElement,
        setSearch,
        setSimpleSearch,
        selectedOption,
        setSelectedOption,
        render,
        setRender,
        color,
        modified,
        setModified
    } = props;

    const {
        option,
        rowsPerPage,
        attendanceStartDate,
        attendanceEndDate,
        results,
        setResults,
        selectedDate,
        setSelectedDate,
        pagination,
        tableId,
        searchId,
        thead,
        tbody,
        handleChange,
        getData,
        loadingDays
    } = DiningAttendanceTableApp({
        setBodyForExport,
        finalArray,
        searchElement,
        selectedOption,
        setRender,
        color,
        modified,
        setModified
    });

    return (
        <>
            {loadingDays && <FullPageLoader id="overlay_attendance_days" portal />}
            <DiningAttendanceHead
                subView={subView}
                handleBtnDinning={handleBtnDinning}
                handleBtnSettings={handleBtnSettings}
                handleBtnDepartmentPolicies={handleBtnDepartmentPolicies}
                handleBtnTotalMeals={handleBtnTotalMeals}
                finalArray={finalArray}
                setFinalArray={setFinalArray}
                setBodyForExport={setBodyForExport}
                searchElement={searchElement}
                setSearchElement={setSearchElement}
                setSearch={setSearch}
                setSimpleSearch={setSimpleSearch}
                setResults={setResults}
                results={results}
                option={option}
                rowsPerPage={rowsPerPage}
                attendanceStartDate={attendanceStartDate}
                attendanceEndDate={attendanceEndDate}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                handleChange={handleChange}
                getData={getData}
            />

            <div className={"col-12"}>
                <CustomTable tableId={tableId}
                             searchId={searchId}
                             checkbox={false}
                             thead={thead}
                             tbody={tbody}
                             columnActions={false}
                             textNotInformation={"it_looks_like_the_cafeteria_is_still_waiting_for_its_first_guest"}
                             withoutHeight={true}
                />
            </div>
            {pagination()}
            {render}
        </>
    );
};

export default DiningAttendanceTable;
